import { forwardRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  deleteAllCurrentOthersSnippets,
  deleteAllCurrentPinnedSnippets,
  isDeleting,
  othersSnippets,
  pinnedSnippets,
  snippetsTabsValue,
  toggleIsDeletingSnippetsPopupDialog,
} from "rtk-slice"

import { isMobile } from "utils/mediaQuery"

import { Divider, Stack, Typography, useTheme } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Slide from "@mui/material/Slide"

import { enqueueSnackbar } from "notistack"
import { DeleteSolid } from "ui-icons"

const Transition = forwardRef((props, ref) => {
  return <Slide direction="down" ref={ref} {...props} />
})

Transition.displayName = "Transition"

const ConfirmDeletingWarningMessage = ({
  type,
  handleDeleteSingleSnippet,
  setSingleSnippet,
  setAllSnippets,
}) => {
  const dispatch = useDispatch()
  const {
    palette: {
      error: { main: error },
      text: { cancel },
    },
  } = useTheme()
  const isDeletingState = useSelector(isDeleting)
  const snippetsTabsValueState = useSelector(snippetsTabsValue)
  const pinnedSnippetsLengthState = useSelector(pinnedSnippets).length
  const othersSnippetsLengthState = useSelector(othersSnippets).length

  const handleToggleIsDeleting = () => {
    dispatch(toggleIsDeletingSnippetsPopupDialog())
    type === "all"
      ? setAllSnippets((prev) => !prev)
      : setSingleSnippet((prev) => !prev)
  }

  const deleteSingleSnippet = () => {
    handleDeleteSingleSnippet()
    handleToggleIsDeleting()
  }

  const handleDeleteAllSnippets = () => {
    if (!snippetsTabsValueState) {
      dispatch(deleteAllCurrentPinnedSnippets())
    }
    if (snippetsTabsValueState) {
      dispatch(deleteAllCurrentOthersSnippets())
    }
    handleToggleIsDeleting()
    enqueueSnackbar(
      `All ${
        !snippetsTabsValueState ? "PINNED" : "OTHERS"
      } snippets have been deleted`
    )
  }

  return (
    <div>
      <Dialog
        open={isDeletingState}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        keepMounted={false}
        onClose={handleToggleIsDeleting}
        aria-label="alert confirm deleting all snippets"
        sx={{
          "& .MuiBackdrop-root": {
            background: "hsl(30% 70% 10% / 0.6)",
            backdropFilter: "blur(10px)",
          },
          "& .MuiPaper-root": {
            backgroundImage: "none",
            bgcolor: "background.confirm",
            borderRadius: "1em",
            borderTop: `1px solid ${error}`,

            "& .MuiDivider-root": {
              borderColor: "gray",
              opacity: 0.4,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "0.5em",
            fontWeight: 900,
            bgcolor: "background.cardHeader",
          }}
          variant={isMobile() ? "h6" : "h5"}
        >
          {isMobile() ? (
            <DeleteSolid width="24" height="24" fill={error} />
          ) : null}
          {type === "all"
            ? `Delete ${
                !snippetsTabsValueState ? "Pinned" : "Others"
              } Snippets`
            : "Delete Snippet"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText
            id="alert-confirm-deleting-all-snippets"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "2em",
            }}
            component="div"
          >
            {!isMobile() ? (
              <Stack justifySelf="flex-start">
                <DeleteSolid width="80" height="80" fill={error} />
              </Stack>
            ) : null}
            {type === "all" ? (
              <Typography component="span" variant="body1">
                {`Do you want to delete ( ${
                  !snippetsTabsValueState
                    ? pinnedSnippetsLengthState
                    : othersSnippetsLengthState
                } ) snippets?`}
                <br />
                <br />
                {"Press"}{" "}
                <span style={{ color: cancel }}>Cancel</span>
                {" to go back."} <br />
                {"Unless you know, what you are doing, "}
                {"press"} <span style={{ color: error }}>DELETE</span>
                {", to continue and completely delete "}
                {!snippetsTabsValueState ? (
                  <b>ALL SHOWN PINNED</b>
                ) : (
                  <b>ALL SHOWN OTHERS</b>
                )}
                {" snippets. "}
              </Typography>
            ) : (
              <Typography
                component="span"
                sx={{
                  flex: 1,
                  fontSize: isMobile() ? "1.4rem" : "inherit",
                }}
                variant="body1"
              >
                {`Do you want to delete this snippet?`}
                <br />
                <br />
                {"Press"}{" "}
                <span style={{ color: cancel }}>Cancel</span>
                {" to go back."} <br />
                {"Unless you know, what you are doing, "}
                {"press"} <span style={{ color: error }}>DELETE</span>
                {", to continue and delete this snippet. "}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: "1em 2em" }}>
          <Button
            onClick={
              type === "all"
                ? handleDeleteAllSnippets
                : deleteSingleSnippet
            }
            sx={{ fontSize: "1.6rem" }}
            color="error"
            size="large"
          >
            Delete
          </Button>
          <Button
            sx={{
              color: cancel,
              fontSize: "1.6rem",
              textTransform: "unset",
            }}
            onClick={handleToggleIsDeleting}
            size="large"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
export default ConfirmDeletingWarningMessage
